import { useState } from 'react';
import { Clock, AlertTriangle, Wrench, ChevronRight } from 'lucide-react';
import RiskBadge from './RiskBadge';
import { cn } from '@/lib/utils';

type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';
type Priority = 'immediate' | 'short-term' | 'medium-term' | 'long-term';
type Effort = 'low' | 'medium' | 'high';

interface RemediationItem {
  id: string;
  title: string;
  description: string;
  riskLevel: RiskLevel;
  priority: Priority;
  effort: Effort;
  estimatedTime: string;
  relatedVulnerability?: string;
  steps: string[];
}

interface PrioritizedRemediationProps {
  items: RemediationItem[];
}

/** A deduplicated group: one representative item + count of occurrences */
interface RemediationGroup {
  representative: RemediationItem;
  count: number;
  allIds: string[];
}

const priorityConfig: Record<Priority, { label: string; className: string; icon: typeof Clock }> = {
  immediate: { label: 'Immediate', className: 'bg-risk-critical text-white', icon: AlertTriangle },
  'short-term': {
    label: 'Short-term (1-2 weeks)',
    className: 'bg-risk-high text-white',
    icon: Clock,
  },
  'medium-term': {
    label: 'Medium-term (1 month)',
    className: 'bg-risk-medium text-foreground',
    icon: Clock,
  },
  'long-term': { label: 'Long-term (3+ months)', className: 'bg-risk-low text-white', icon: Clock },
};

const effortConfig: Record<Effort, { label: string; className: string }> = {
  low: { label: 'Low Effort', className: 'bg-risk-low-bg text-risk-low' },
  medium: { label: 'Medium Effort', className: 'bg-risk-medium-bg text-risk-medium' },
  high: { label: 'High Effort', className: 'bg-risk-high-bg text-risk-high' },
};

const riskOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

/** Deduplicate items by title, keeping the highest-severity representative */
function deduplicateByTitle(items: RemediationItem[]): RemediationGroup[] {
  const map = new Map<string, RemediationGroup>();
  for (const item of items) {
    const key = item.title;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, { representative: item, count: 1, allIds: [item.id] });
    } else {
      existing.count++;
      existing.allIds.push(item.id);
      // Keep the higher-severity item as representative
      if ((riskOrder[item.riskLevel] ?? 4) < (riskOrder[existing.representative.riskLevel] ?? 4)) {
        existing.representative = item;
      }
    }
  }
  return Array.from(map.values());
}

const RemediationEntry = ({ group }: { group: RemediationGroup }) => {
  const [expanded, setExpanded] = useState(false);
  const item = group.representative;

  return (
    <div className="border rounded-lg transition-all hover:shadow-sm">
      <button
        type="button"
        className="w-full flex items-center gap-3 p-4 text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.title}`}
      >
        <ChevronRight
          className={cn(
            'w-4 h-4 shrink-0 text-muted-foreground transition-transform duration-200',
            expanded && 'rotate-90'
          )}
        />
        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
          <RiskBadge level={item.riskLevel} size="sm" />
          <span className="font-semibold text-sm truncate">{item.title}</span>
          {group.count > 1 && (
            <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground shrink-0">
              {group.count} occurrences
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className={cn('text-xs px-2 py-1 rounded-full', effortConfig[item.effort].className)}
          >
            {effortConfig[item.effort].label}
          </span>
          <span className="text-xs text-muted-foreground hidden sm:inline">
            {item.estimatedTime}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-0 ml-7 space-y-3 animate-slide-up">
          <p className="text-sm text-muted-foreground">{item.description}</p>

          {group.allIds.length > 0 && (
            <div className="text-xs text-muted-foreground flex flex-wrap gap-1 items-center">
              <span>Related:</span>
              {group.allIds.map((id) => (
                <code key={id} className="bg-muted px-1 rounded">
                  {id}
                </code>
              ))}
            </div>
          )}

          <div className="bg-risk-low-bg/50 rounded-lg p-3 border border-risk-low/20">
            <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
              <Wrench className="w-3 h-3" />
              Remediation Steps
            </h5>
            <ol className="space-y-2">
              {item.steps.map((step, i) => (
                <li key={i} className="text-sm flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-risk-low text-white text-xs flex items-center justify-center">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};

const PrioritizedRemediation = ({ items }: PrioritizedRemediationProps) => {
  if (items.length === 0) {
    return (
      <div className="card-elevated p-8 animate-slide-up flex flex-col items-center gap-3 text-center">
        <Wrench className="w-10 h-10 text-green-500 opacity-80" />
        <p className="font-semibold text-sm">No remediation actions required</p>
        <p className="text-xs text-muted-foreground max-w-sm">
          No actionable findings were detected in this scan. Continue regular security monitoring to
          maintain this posture.
        </p>
      </div>
    );
  }

  // Group by priority, then deduplicate by title within each group
  const groupedItems = items.reduce(
    (acc, item) => {
      if (!acc[item.priority]) acc[item.priority] = [];
      acc[item.priority].push(item);
      return acc;
    },
    {} as Record<Priority, RemediationItem[]>
  );

  const priorityOrder: Priority[] = ['immediate', 'short-term', 'medium-term', 'long-term'];

  return (
    <div className="space-y-6">
      {priorityOrder.map((priority) => {
        const priorityItems = groupedItems[priority];
        if (!priorityItems || priorityItems.length === 0) return null;

        const deduplicated = deduplicateByTitle(priorityItems);
        const config = priorityConfig[priority];
        const PriorityIcon = config.icon;

        return (
          <div key={priority} className="card-elevated p-6 animate-slide-up">
            <div className="flex items-center gap-3 mb-4">
              <div className={cn('p-2 rounded-lg', config.className)}>
                <PriorityIcon className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-semibold">{config.label}</h3>
              <span className="ml-auto bg-muted px-3 py-1 rounded-full text-sm">
                {deduplicated.length} type{deduplicated.length > 1 ? 's' : ''}
                {priorityItems.length !== deduplicated.length && (
                  <span className="text-muted-foreground">
                    {' '}({priorityItems.length} total)
                  </span>
                )}
              </span>
            </div>

            <div className="space-y-2">
              {deduplicated.map((group) => (
                <RemediationEntry key={group.representative.id} group={group} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PrioritizedRemediation;
